import React, { useEffect, useState } from 'react';
import './BankSelection.scss';
import NoEligibleAccounts from './NoEligibleAccounts';
import {
  STEP_BANK_FAILURE,
  STEP_BANK_SUCCESS,
  STEP_LANDING,
  TECHNICAL_BANK_FAILURE,
  STEP_BANK_ADDED,
  STEP_BANK_ADDED_PARTIALLY,
} from '../AddBank/AddBank';
import NewBankSelect from '../NewBankSelect/NewBankSelect';
import Button from '../../Shared/Button/Button';
import If from '../../Shared/If/If';
import Loader from '../../Shared/Loader/Loader';
import TRP from '../../Shared/TRP/TRP';
import { useSelector } from 'react-redux';
import {
  bankAddedPartially,
  bankAddedSuccessfully,
  getPlaidAddBank,
  isBankDuplicated,
} from '../../../redux/modules/linkedBanks/linkBank.selectors';

const BankSelection = (props) => {
  const {
    dispatchPageLoad,
    actions,
    actions: {
      registerNewBank,
      getExistingBanks,
      addLinkedBank,
      incrementLinkAttempts,
      logLinkFailure,
    },
    linkedBanks: {
      bankAccounts,
      banksLoaded,
      banksLoading,
      addBankLoading,
      authId,
      hasFailed,
    },
    setBankProgress,
  } = props;

  const bankAdded = useSelector(getPlaidAddBank);
  const addedSuccessfully = useSelector(bankAddedSuccessfully);
  const addedPartially = useSelector(bankAddedPartially);
  const isBankDoubled = useSelector(isBankDuplicated);
  const acctVerbiage = accountContext(bankAccounts);
  const hasAccounts = !addBankLoading && bankAccounts.length;
  const [selectedNewBank, setSelectedNewBank] = useState(bankAccounts[0]);
  const [verificationLoading /*setVerificationLoading*/] = useState(false);

  function multiFundValidation() {
    if (bankAdded === 'SUCCESSFUL') {
      if (isBankDoubled) {
        setBankProgress(STEP_BANK_ADDED);
        return null;
      }
      if (addedPartially) {
        setBankProgress(STEP_BANK_ADDED_PARTIALLY);
        return null;
      }
      if (addedSuccessfully !== undefined && addedSuccessfully === true) {
        registerNewBank(selectedNewBank || bankAccounts[0], getExistingBanks);
        setBankProgress(STEP_BANK_SUCCESS);
        return null;
      } else if (addedSuccessfully !== undefined) {
        const errorInfo = {
          reason: 'TRP NAME MISMATCH',
          status: 'fail',
          token: selectedNewBank.referenceId || bankAccounts[0].referenceId,
        };
        incrementLinkAttempts();
        logLinkFailure(errorInfo);
        setBankProgress(STEP_BANK_FAILURE);
        return null;
      }
    } else if (bankAdded === 'FAILED') {
      setBankProgress(STEP_BANK_FAILURE);
      return null;
    }
    return null;
  }
  useEffect(() => {
    if (hasFailed && !banksLoading) {
      setBankProgress(TECHNICAL_BANK_FAILURE);
    }
    if (
      bankAdded !== 'SUCCESSFUL' &&
      bankAdded !== 'FAILED' &&
      banksLoaded &&
      !banksLoading &&
      !hasFailed
    ) {
      addLinkedBank({
        plaidBankConstraint: {
          authenticationId: authId,
          referenceId:
            selectedNewBank?.referenceId || bankAccounts[0]?.referenceId,
        },
      });
    }
    if (bankAccounts.length === 1) {
      if (bankAdded === 'SUCCESSFUL' && banksLoaded && !hasFailed) {
        if (isBankDoubled && !addedSuccessfully) {
          setBankProgress(STEP_BANK_ADDED);
        }
        if (
          addedSuccessfully !== undefined &&
          addedSuccessfully === true &&
          !isBankDoubled
        ) {
          registerNewBank(selectedNewBank || bankAccounts[0], getExistingBanks);
          setBankProgress(STEP_BANK_SUCCESS);
        } else if (addedSuccessfully !== undefined && !isBankDoubled) {
          const errorInfo = {
            reason: 'TRP NAME MISMATCH',
            status: 'fail',
            token: selectedNewBank?.referenceId || bankAccounts[0]?.referenceId,
          };
          incrementLinkAttempts();
          logLinkFailure(errorInfo);
          setBankProgress(STEP_BANK_FAILURE);
        }
      } else if (bankAdded === 'FAILED' && banksLoaded && !hasFailed) {
        setBankProgress(STEP_BANK_FAILURE);
      }
    }
  }, [
    hasFailed,
    bankAdded,
    banksLoaded,
    banksLoading,
    bankAccounts,
    addLinkedBank,
    authId,
    getExistingBanks,
    registerNewBank,
    selectedNewBank,
    setBankProgress,
    incrementLinkAttempts,
    logLinkFailure,
    addedSuccessfully,
    isBankDoubled,
  ]);

  function back() {
    setBankProgress(STEP_LANDING);
    dispatchPageLoad('Add New Bank Landing Page');
  }

  function accountContext(accts) {
    switch (accts.length) {
      case 0:
        return 'We could not locate any accounts at this bank.';
      case 1:
        return 'We located one account at this bank:';
      default:
        return 'We located multiple accounts at this bank:';
    }
  }

  return (
    <div className="bank-selection-bank-modal">
      <If test={addBankLoading}>
        <div className="bank-selection-loader-container">
          <div className="bank-selection-loading-text">
            Adding your account...
          </div>
          <Loader innerClassName="bank-selection-loader" isVisible />
        </div>
      </If>

      <If test={hasAccounts}>
        <div className="bank-selection-modal-heading">Choose your account</div>
      </If>

      <If test={banksLoading}>
        <div className="bank-selection-loader-container">
          <div className="bank-selection-loading-text">
            Syncing your accounts...
          </div>
          <Loader innerClassName="bank-selection-loader" isVisible />
        </div>
      </If>

      <If test={banksLoaded && hasAccounts && !addBankLoading}>
        <div className="bank-selection-results-container">
          <div className="bank-selection-result-heading">
            {`${acctVerbiage}`}
          </div>
          <div className="bank-selection-sub-text">
            Choose the bank account you would like to link to your <TRP />{' '}
            account.
          </div>
          <div className="bank-selection-account-container">
            {bankAccounts.map((bank, i) => (
              <NewBankSelect
                key={i}
                actions={actions}
                addBank
                bank={bank}
                bankIndex={i}
                selectedNewBank={selectedNewBank}
                updateSelectedBank={() => setSelectedNewBank(bank)}
              />
            ))}
          </div>
        </div>
      </If>

      <If test={hasAccounts}>
        <div className="bank-selection-actions-container">
          <Button buttonType="secondary" onClick={() => back()}>
            Back
          </Button>
          <Button
            disabled={!selectedNewBank}
            className="e2e-add-bank-selection-next"
            buttonType="primary"
            loading={verificationLoading}
            onClick={() => multiFundValidation()}
          >
            Link Bank
          </Button>
        </div>
      </If>

      <If test={!addBankLoading && banksLoaded && !hasAccounts}>
        <NoEligibleAccounts
          dispatchPageLoad={dispatchPageLoad}
          setBankProgress={setBankProgress}
        />
      </If>
    </div>
  );
};

export default BankSelection;
